/**
 * Spline blending of motion keys (lane C0-WEAPON; the motion rule: "keys blended with eased/spline interpolation, no
 * kinks at the keys"). `motionKeys` (index.ts) gives the two keys a motion frame lies between; this adds the key
 * before and the key after, so the skeleton (rig/skeleton.ts) can run every joint along a Catmull-Rom curve through
 * the keys instead of a straight eased line that stops dead on each key.
 *
 * Timing: a step marked `in` (gathering speed into a blow) or `out` (a follow-through settling) keeps its ease as a
 * time warp; `inOut` (the default) runs at an even pace, because the Hermite curve already eases where the motion
 * turns back (its tangent there is zero) and carries the speed through where it goes on.
 */
import { FRAMES, type Ease } from './kit';
import { MOTIONS, type Pose } from './index';

export interface MotionSpan {
  /** The key before `a` (or `a` itself at the start of a one-shot), `a`, `b`, and the key after `b`. */
  p: Pose;
  a: Pose;
  b: Pose;
  n: Pose;
  /** 0–1 between `a` and `b`, time-warped by an `in` or `out` step. */
  u: number;
}

const warp = (e: Ease, t: number): number => (e === 'in' ? t * t : e === 'out' ? 1 - (1 - t) * (1 - t) : t);

/** The four keys round a motion frame and how far along it lies (same frame layout as `motionKeys`). */
export function motionSpan(ph: number): MotionSpan {
  const m = MOTIONS[Math.floor(ph / FRAMES) % MOTIONS.length]!;
  const f = ph % FRAMES;
  const k = m.keys;
  const n = k.length;
  const loop = 'loop' in m && m.loop;
  const pos = loop ? (f / FRAMES) * n : (f / (FRAMES - 1)) * (n - 1);
  const i = Math.min(loop ? n - 1 : n - 2, Math.floor(pos));
  const ease: readonly Ease[] = 'ease' in m ? m.ease : [];
  const u = warp(ease[i] ?? 'inOut', Math.max(0, Math.min(1, pos - i)));
  const before = 'before' in m ? (m.before as Pose | undefined) : undefined;
  const after = 'after' in m ? (m.after as Pose | undefined) : undefined;
  const at = (j: number): Pose =>
    loop
      ? k[((j % n) + n) % n]!
      : j < 0 && before
        ? before
        : j > n - 1 && after
          ? after
          : k[Math.max(0, Math.min(n - 1, j))]!;
  return { p: at(i - 1), a: at(i), b: at(i + 1), n: at(i + 2), u };
}

/** Hermite basis at `u`: weight of the far key (`s`, a smoothstep) and of the two tangents. */
export function hermite(u: number): { s: number; m0: number; m1: number } {
  const u2 = u * u;
  const u3 = u2 * u;
  return { s: 3 * u2 - 2 * u3, m0: u3 - 2 * u2 + u, m1: u3 - u2 };
}
